<template>
    <div class="rounded-md border border-valence-grey-250 bg-white p-2.5 flex flex-col gap-1.5">
        <input
            ref="inputRef"
            v-model="title"
            type="text"
            :aria-label="titleAriaLabel"
            placeholder="Action title"
            class="w-full rounded-md border border-valence-grey-300 bg-white px-3 py-1.5 body-small text-valence-grey-900 placeholder:text-valence-grey-400 focus:outline-none focus:ring-1 focus:ring-valence-primary-400"
            @keydown.enter.prevent="handleSubmit"
            @keydown.esc.prevent="emit('cancel')" />
        <textarea
            ref="textareaRef"
            v-model="description"
            :aria-label="descriptionAriaLabel"
            placeholder="Description (optional)"
            class="resize-none overflow-hidden w-full rounded-md border border-valence-grey-300 bg-white px-3 py-1.5 body-small text-valence-grey-750 placeholder:text-valence-grey-400 focus:outline-none focus:ring-1 focus:ring-valence-primary-400"
            @input="autoResizeTextarea"
            @keydown.esc.prevent="emit('cancel')" />
        <div class="flex items-center justify-end gap-2 pt-1">
            <BaseButton theme="neutral" size="sm" @click="emit('cancel')">Cancel</BaseButton>
            <BaseButton theme="primary" size="sm" :disabled="!title.trim() || isSubmitting" @click="handleSubmit">
                {{ isSubmitting ? "Saving…" : "Save" }}
            </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import { nextTick, onMounted, ref, watch } from "vue";

const props = defineProps<{
    initialTitle?: string;
    initialDescription?: string;
    isSubmitting?: boolean;
    titleAriaLabel?: string;
    descriptionAriaLabel?: string;
}>();

const emit = defineEmits<{
    submit: [title: string, description: string];
    cancel: [];
}>();

function cleanText(value: string): string {
    return value.replace(/\s+/g, " ").trim();
}

const title = ref(props.initialTitle ?? "");
const description = ref(props.initialDescription ? cleanText(props.initialDescription) : "");
const inputRef = ref<HTMLInputElement | null>(null);
const textareaRef = ref<HTMLTextAreaElement | null>(null);

function autoResizeTextarea() {
    const el = textareaRef.value;
    if (!el) {
        return;
    }
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
}

watch(
    () => props.initialTitle,
    (val) => {
        title.value = val ?? "";
    },
);
watch(
    () => props.initialDescription,
    async (val) => {
        description.value = val ? cleanText(val) : "";
        await nextTick();
        autoResizeTextarea();
    },
);

onMounted(() => autoResizeTextarea());

function handleSubmit() {
    if (!title.value.trim() || props.isSubmitting) {
        return;
    }
    emit("submit", cleanText(title.value), cleanText(description.value));
}

async function focus() {
    await nextTick();
    inputRef.value?.focus();
}

function reset() {
    title.value = "";
    description.value = "";
}

defineExpose({ focus, reset });
</script>
